import { translate, type CopyKey } from '@ticarishift/i18n';
import {
  Ban,
  Briefcase,
  CalendarClock,
  CalendarHeart,
  CalendarMinus,
  CalendarOff,
  CalendarPlus,
  CalendarX,
  Check,
  CircleAlert,
  CircleCheck,
  CircleDashed,
  CircleDot,
  CircleX,
  CircleMinus,
  ClockAlert,
  ClockArrowDown,
  ClockArrowUp,
  Coffee,
  Contrast,
  DoorOpen,
  Flag,
  FlagTriangleRight,
  Hourglass,
  Info,
  Lock,
  Pencil,
  Send,
  ShieldAlert,
  ShieldX,
  Sofa,
  Stethoscope,
  TreePalm,
  TriangleAlert,
  UserX,
  WifiOff,
  type LucideIcon,
} from 'lucide-react';

/**
 * Devam durumu sözlüğü v1 (08 §5.7 A–E, UX-19). Tek kaynaktır: StatusBadge, DayCell, ShiftChip, takvim,
 * grafik ve lejantlar buradan okur. Kural: izin türlerinin hepsi `leave` ailesindedir (mor); kırmızı
 * (`danger`) yalnız devamsızlık, ret ve hata içindir. Renk ve ikon koda değil anlamına bağlıdır; görünen
 * kod ve etiket metin kataloğundadır (`status.*`).
 */
export type StatusFamily = 'ok' | 'attention' | 'action' | 'danger' | 'info' | 'break' | 'leave' | 'extra' | 'rest' | 'neutral';

/** Rengin yanında anlam taşıyan görsel işaret (08 §5.7; renk tek başına bilgi taşımaz). */
export type StatusPattern = 'hatch' | 'half' | 'dotted' | 'dashed' | 'strike' | 'pulse' | 'strong';

export type StatusEntry = {
  readonly family: StatusFamily;
  /** lucide adı (kebab-case); kurulu sürümde varlığı test edilir. */
  readonly iconName: string | null;
  readonly icon: LucideIcon | null;
  readonly label: CopyKey;
  /** Görünen kısa kod (ör. ÇX); tenant yeniden adlandırabilir. */
  readonly code?: CopyKey;
  readonly pattern?: StatusPattern;
};

const entry = (
  family: StatusFamily,
  icon: [string, LucideIcon] | null,
  label: CopyKey,
  extra: { code?: CopyKey; pattern?: StatusPattern } = {},
): StatusEntry => ({ family, iconName: icon?.[0] ?? null, icon: icon?.[1] ?? null, label, ...extra });

export const statusDictionary = {
  // A. Puantaj gün kodları (04 §6.1 sırasıyla).
  'day.N': entry('ok', ['check', Check], 'status.day.N.label', { code: 'status.day.N.code' }),
  'day.HT': entry('rest', ['sofa', Sofa], 'status.day.HT.label', { code: 'status.day.HT.code', pattern: 'hatch' }),
  'day.HTC': entry('extra', ['calendar-clock', CalendarClock], 'status.day.HTC.label', { code: 'status.day.HTC.code' }),
  'day.RT': entry('rest', ['flag', Flag], 'status.day.RT.label', { code: 'status.day.RT.code' }),
  'day.AY': entry('rest', ['flag', Flag], 'status.day.AY.label', { code: 'status.day.AY.code', pattern: 'half' }),
  'day.RTC': entry('extra', ['calendar-clock', CalendarClock], 'status.day.RTC.label', { code: 'status.day.RTC.code' }),
  'day.ST': entry('rest', ['calendar-off', CalendarOff], 'status.day.ST.label', { code: 'status.day.ST.code', pattern: 'hatch' }),
  'day.YI': entry('leave', ['tree-palm', TreePalm], 'status.day.YI.label', { code: 'status.day.YI.code' }),
  'day.MI': entry('leave', ['calendar-heart', CalendarHeart], 'status.day.MI.label', { code: 'status.day.MI.code' }),
  'day.UI': entry('leave', ['calendar-minus', CalendarMinus], 'status.day.UI.label', { code: 'status.day.UI.code', pattern: 'hatch' }),
  'day.R': entry('leave', ['stethoscope', Stethoscope], 'status.day.R.label', { code: 'status.day.R.code' }),
  'day.D': entry('danger', ['user-x', UserX], 'status.day.D.label', { code: 'status.day.D.code' }),
  'day.GO': entry('ok', ['briefcase', Briefcase], 'status.day.GO.label', { code: 'status.day.GO.code' }),
  'day.PC': entry('neutral', ['calendar-x', CalendarX], 'status.day.PC.label', { code: 'status.day.PC.code', pattern: 'dotted' }),
  'day.X': entry('neutral', null, 'status.day.X.label', { code: 'status.day.X.code' }),
  'day.CX': entry('action', ['door-open', DoorOpen], 'status.day.CX.label', { code: 'status.day.CX.code' }),

  // B. Saat kovaları (satır toplamı ve gün detayı).
  'bucket.FM': entry('extra', null, 'status.bucket.FM.label', { code: 'status.bucket.FM.code' }),
  'bucket.FSC': entry('extra', null, 'status.bucket.FSC.label', { code: 'status.bucket.FSC.code' }),
  'bucket.GC': entry('neutral', null, 'status.bucket.GC.label', { code: 'status.bucket.GC.code' }),
  'bucket.G11': entry('neutral', null, 'status.bucket.G11.label', { code: 'status.bucket.G11.code' }),
  'bucket.IB': entry('neutral', null, 'status.bucket.IB.label', { code: 'status.bucket.IB.code' }),
  'bucket.UNDER': entry('attention', null, 'status.bucket.UNDER.label', { code: 'status.bucket.UNDER.code' }),

  // C. Hücre işaretleri.
  'flag.late': entry('attention', ['clock-alert', ClockAlert], 'status.flag.late.label', { code: 'status.flag.late.code' }),
  'flag.leftEarly': entry('attention', ['clock-arrow-down', ClockArrowDown], 'status.flag.leftEarly.label', { code: 'status.flag.leftEarly.code' }),
  'flag.earlyArrival': entry('neutral', ['clock-arrow-up', ClockArrowUp], 'status.flag.earlyArrival.label', { code: 'status.flag.earlyArrival.code' }),
  'flag.pendingDecision': entry('attention', ['hourglass', Hourglass], 'status.flag.pendingDecision.label', { code: 'status.flag.pendingDecision.code' }),
  'flag.violation': entry('danger', ['triangle-alert', TriangleAlert], 'status.flag.violation.label', { code: 'status.flag.violation.code' }),
  'flag.corrected': entry('neutral', ['pencil', Pencil], 'status.flag.corrected.label', { code: 'status.flag.corrected.code' }),
  'flag.workedOnLeave': entry('attention', ['calendar-plus', CalendarPlus], 'status.flag.workedOnLeave.label', { code: 'status.flag.workedOnLeave.code' }),

  // D. Canlı durumlar.
  'live.expected': entry('neutral', ['circle-dashed', CircleDashed], 'status.live.expected.label', { pattern: 'dashed' }),
  'live.inside': entry('info', ['circle-dot', CircleDot], 'status.live.inside.label', { pattern: 'pulse' }),
  'live.onBreak': entry('break', ['coffee', Coffee], 'status.live.onBreak.label'),
  'live.late': entry('attention', ['clock-alert', ClockAlert], 'status.live.late.label'),
  'live.absent': entry('danger', ['user-x', UserX], 'status.live.absent.label'),
  'live.leftEarly': entry('attention', ['clock-arrow-down', ClockArrowDown], 'status.live.leftEarly.label'),
  'live.done': entry('ok', ['circle-check', CircleCheck], 'status.live.done.label'),
  'live.missingCheckout': entry('action', ['door-open', DoorOpen], 'status.live.missingCheckout.label'),
  'live.onLeave': entry('leave', ['tree-palm', TreePalm], 'status.live.onLeave.label'),
  'live.review': entry('action', ['shield-alert', ShieldAlert], 'status.live.review.label'),
  'live.rejected': entry('danger', ['shield-x', ShieldX], 'status.live.rejected.label'),

  // E. Süreç durumları.
  'request.pending': entry('attention', ['hourglass', Hourglass], 'status.request.pending.label'),
  'request.approved': entry('ok', ['circle-check', CircleCheck], 'status.request.approved.label'),
  'request.rejected': entry('danger', ['ban', Ban], 'status.request.rejected.label'),
  'request.cancelPending': entry('attention', ['hourglass', Hourglass], 'status.request.cancelPending.label'),
  'request.cancelled': entry('neutral', null, 'status.request.cancelled.label', { pattern: 'strike' }),
  'request.failed': entry('action', ['circle-alert', CircleAlert], 'status.request.failed.label'),
  'request.withdrawn': entry('neutral', null, 'status.request.withdrawn.label', { pattern: 'strike' }),
  // Telafi borcu (S37; 4857 md. 64): açık borç telafi bekler, iptal edilen silinmez üstü çizilir.
  'makeup.open': entry('attention', ['hourglass', Hourglass], 'status.makeup.open.label'),
  'makeup.cancelled': entry('neutral', null, 'status.makeup.cancelled.label', { pattern: 'strike' }),
  // Telafi işaretli atama (planlayıcı, puantaj): borca sayılan vardiya.
  'makeup.marked': entry('action', null, 'status.makeup.marked.label'),
  'period.open': entry('neutral', null, 'status.period.open.label'),
  'period.inReview': entry('attention', ['hourglass', Hourglass], 'status.period.inReview.label'),
  'period.approved': entry('ok', ['circle-check', CircleCheck], 'status.period.approved.label'),
  'period.locked': entry('neutral', ['lock', Lock], 'status.period.locked.label', { pattern: 'strong' }),
  'period.exported': entry('info', ['send', Send], 'status.period.exported.label'),
  'roster.draft': entry('neutral', null, 'status.roster.draft.label', { pattern: 'dashed' }),
  'roster.published': entry('ok', ['circle-check', CircleCheck], 'status.roster.published.label'),
  'roster.changedAfterPublish': entry('attention', ['circle-dot', CircleDot], 'status.roster.changedAfterPublish.label'),
  // Planlama kuralının seviyesi (04 §5.2; S22-a ihlal paneli): ENGEL kırmızı, UYARI sarı, BİLGİ mavi.
  'plan.engel': entry('danger', ['circle-x', CircleX], 'status.plan.engel.label'),
  'plan.uyari': entry('attention', ['triangle-alert', TriangleAlert], 'status.plan.uyari.label'),
  'plan.bilgi': entry('info', ['info', Info], 'status.plan.bilgi.label'),
  // Kayıt anındaki kural işareti (S20 Uyarılar v2, kontrol listesi): İŞARET kaydı engellemez, incelenir (amber).
  'plan.isaret': entry('attention', ['flag-triangle-right', FlagTriangleRight], 'status.plan.isaret.label'),
  // QR Ekranının sağlığı (S25; 06 §8.4, CHK-59): son heartbeat 3 dk içinde çevrimiçi, 3–10 dk gecikiyor, daha eskisi
  // çevrimdışı; kod bağlandı ama ekran eşleştirmeyi tamamlamadıysa "Kurulum bekliyor".
  'display.online': entry('ok', ['circle-check', CircleCheck], 'status.display.online.label'),
  'display.delayed': entry('attention', ['clock-alert', ClockAlert], 'status.display.delayed.label'),
  'display.offline': entry('action', ['wifi-off', WifiOff], 'status.display.offline.label'),
  'display.pendingSetup': entry('neutral', ['hourglass', Hourglass], 'status.display.pendingSetup.label', { pattern: 'dashed' }),
  'display.unpaired': entry('neutral', null, 'status.display.unpaired.label'),
  'display.revoked': entry('danger', ['ban', Ban], 'status.display.revoked.label'),
  // Çalışanın telefonunun mesai kaydı güvenilirliği (S25; 06 §4.7, CHK-22): onay bekleyen sarı; ret kayıt engeli değil,
  // yöneticinin kararıdır (kırmızı değil).
  'device.trusted': entry('ok', ['circle-check', CircleCheck], 'status.device.trusted.label'),
  'device.pending': entry('attention', ['hourglass', Hourglass], 'status.device.pending.label'),
  'device.rejected': entry('neutral', ['ban', Ban], 'status.device.rejected.label'),
  'device.removed': entry('neutral', null, 'status.device.removed.label', { pattern: 'strike' }),
  // Zamanlanmış raporun son gönderimi (S15, F0-C4): başarısız gönderim "Uygulanamadı" gibi eylem ister (turuncu).
  'delivery.sent': entry('ok', ['circle-check', CircleCheck], 'status.delivery.sent.label'),
  'delivery.failed': entry('action', ['circle-alert', CircleAlert], 'status.delivery.failed.label'),
  'delivery.never': entry('neutral', null, 'status.delivery.never.label', { pattern: 'dashed' }),
  // Fazla süre adayı kararı (S20; 04 §7.1): onay `request.approved`, bekleyen `flag.pendingDecision`. "Çalışma sayma" ret
  // değildir (kırmızı değil); otomatik sayılan kalem de nötrdür.
  'decision.notWork': entry('neutral', ['circle-minus', CircleMinus], 'status.decision.notWork.label'),
  'decision.auto': entry('neutral', ['circle-check', CircleCheck], 'status.decision.auto.label'),
  // Tahmini işçilik maliyeti (S58; DE-35): ay kilitlenmeden ya da günleri hesaplanmadan tutar değişebilir.
  'cost.provisional': entry('attention', ['hourglass', Hourglass], 'status.cost.provisional.label'),

  // Yarım gün izin ayrı kod değildir; izin kodu + "½" ile ve bu ikonla gösterilir (04 §6.2).
  'leave.half': entry('leave', ['contrast', Contrast], 'status.day.YI.label', { pattern: 'half' }),
} as const satisfies Record<string, StatusEntry>;

export type StatusKey = keyof typeof statusDictionary;

/**
 * Bugünkü API değerlerinin sözlük karşılıkları (S04). Eski API'nin durum adları yeni UI'da doğrudan
 * gösterilmez: `MissingCheckout` "Bitişi eksik" (ÇX), bütün izin türleri `leave` ailesidir.
 */
export const apiStatusMap = {
  sessionStatus: {
    Open: 'live.inside',
    Closed: 'live.done',
    MissingCheckout: 'live.missingCheckout',
    OnLeave: 'live.onLeave',
  },
  requestStatus: {
    Pending: 'request.pending',
    Approved: 'request.approved',
    Rejected: 'request.rejected',
    Cancelled: 'request.cancelled',
    Failed: 'request.failed',
  },
  // S33: İzin 2.0 talebinin durumu (`TimeOffRequestStatus`): geri çekme ve iptal onayı bekleyen izin.
  timeOffStatus: {
    Pending: 'request.pending',
    Approved: 'request.approved',
    Rejected: 'request.rejected',
    Cancelled: 'request.cancelled',
    Failed: 'request.failed',
    Withdrawn: 'request.withdrawn',
    CancellationPending: 'request.cancelPending',
  },
  leaveType: {
    Annual: 'day.YI',
    Sick: 'day.R',
    Excused: 'day.MI',
    Unpaid: 'day.UI',
  },
  // Zamanlanmış raporun `lastSendStatus` değeri (string enum, F0-C4).
  reportSendStatus: {
    Never: 'delivery.never',
    Sent: 'delivery.sent',
    Failed: 'delivery.failed',
  },
} as const satisfies Record<string, Record<string, StatusKey>>;

/** Aile başına sınıflar. Tailwind taraması için tam sınıf adları sabit yazılır. */
export const familyClasses: Record<StatusFamily, { soft: string; solid: string; outline: string; text: string }> = {
  ok: { soft: 'bg-status-ok-bg text-status-ok', solid: 'bg-status-ok text-surface', outline: 'border border-status-ok text-status-ok', text: 'text-status-ok' },
  attention: { soft: 'bg-status-attention-bg text-status-attention', solid: 'bg-status-attention text-surface', outline: 'border border-status-attention text-status-attention', text: 'text-status-attention' },
  action: { soft: 'bg-status-action-bg text-status-action', solid: 'bg-status-action text-surface', outline: 'border border-status-action text-status-action', text: 'text-status-action' },
  danger: { soft: 'bg-status-danger-bg text-status-danger', solid: 'bg-status-danger text-surface', outline: 'border border-status-danger text-status-danger', text: 'text-status-danger' },
  info: { soft: 'bg-status-info-bg text-status-info', solid: 'bg-status-info text-surface', outline: 'border border-status-info text-status-info', text: 'text-status-info' },
  break: { soft: 'bg-status-break-bg text-status-break', solid: 'bg-status-break text-surface', outline: 'border border-status-break text-status-break', text: 'text-status-break' },
  leave: { soft: 'bg-status-leave-bg text-status-leave', solid: 'bg-status-leave text-surface', outline: 'border border-status-leave text-status-leave', text: 'text-status-leave' },
  extra: { soft: 'bg-status-extra-bg text-status-extra', solid: 'bg-status-extra text-surface', outline: 'border border-status-extra text-status-extra', text: 'text-status-extra' },
  rest: { soft: 'bg-status-rest-bg text-status-rest', solid: 'bg-status-rest text-surface', outline: 'border border-status-rest text-status-rest', text: 'text-status-rest' },
  neutral: { soft: 'bg-surface-muted text-fg-muted', solid: 'bg-fg-muted text-surface', outline: 'border border-strong text-fg-muted', text: 'text-fg-muted' },
};

/**
 * Sunucunun puantaj gün kodu (04 §6.1; "ÇX", "Yİ", "HTÇ") → sözlük anahtarı. Kodlar kataloğun `status.day.*.code`
 * metinleriyle aynıdır (kanonik kod); eşleme katalogdan kurulur, kodda Türkçe dize tutulmaz. Vardiya kodu ("S", "S/A")
 * ve bilinmeyen kod `null`dır.
 */
export function statusOfCode(code: string | null | undefined): StatusKey | null {
  if (!code) {
    return null;
  }

  dayCodes ??= new Map(
    (Object.keys(statusDictionary) as StatusKey[])
      .filter((key) => key.startsWith('day.'))
      .flatMap((key) => {
        const entry: StatusEntry = statusDictionary[key];
        return entry.code ? [[translate(entry.code), key] as const] : [];
      }),
  );
  return dayCodes.get(code) ?? null;
}

let dayCodes: Map<string, StatusKey> | undefined;
/** Hücre işaretinin adı (`flag.late` → `late`); DayCell'in `DayCellFlag` kümesiyle aynıdır. */
export type StatusFlag = Extract<StatusKey, `flag.${string}`> extends `flag.${infer Name}` ? Name : never;

let symbolFlags: Map<string, StatusFlag> | undefined;

/**
 * Sunucunun hücre simgesi (04 §6.1 "İşaretler"; "G⁺", "EÇ", "⋯", "✕") → hücre işareti (`flag.*` anahtarının adı: `late`,
 * `violation`…). Katalogdaki `status.flag.*.code` metinlerinden kurulur; bilinmeyen simge `null`dır.
 */
export function flagOfSymbol(symbol: string): StatusFlag | null {
  symbolFlags ??= new Map(
    (Object.keys(statusDictionary) as StatusKey[])
      .filter((key) => key.startsWith('flag.'))
      .flatMap((key) => {
        const entry: StatusEntry = statusDictionary[key];
        return entry.code ? [[translate(entry.code), key.slice('flag.'.length) as StatusFlag] as const] : [];
      }),
  );
  return symbolFlags.get(symbol) ?? null;
}
